/**
 * Web Audio synthesizer for gift sound effects.
 * No external assets — pure code. Plays nothing if the browser blocks audio.
 */

let ctx = null;
function getCtx() {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  } catch {
    return null;
  }
  return ctx;
}

function envelope(gain, t0, attack, decay, peak = 0.4, sustain = 0.0) {
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(peak, t0 + attack);
  gain.gain.exponentialRampToValueAtTime(Math.max(sustain, 0.0001), t0 + attack + decay);
}

function tone(freq, t0, duration, type = "sine", peak = 0.3) {
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  envelope(gain, t0, 0.02, duration, peak);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
}

function noiseBurst(t0, duration, peak = 0.15, filterFreq = 1200) {
  const c = getCtx();
  if (!c) return;
  const buf = c.createBuffer(1, c.sampleRate * duration, c.sampleRate);
  const out = buf.getChannelData(0);
  for (let i = 0; i < out.length; i++) out[i] = (Math.random() * 2 - 1) * 0.4;
  const src = c.createBufferSource();
  src.buffer = buf;
  const filt = c.createBiquadFilter();
  filt.type = "bandpass";
  filt.frequency.value = filterFreq;
  const gain = c.createGain();
  envelope(gain, t0, 0.01, duration, peak);
  src.connect(filt).connect(gain).connect(c.destination);
  src.start(t0);
  src.stop(t0 + duration + 0.05);
}

function sweep(freqStart, freqEnd, t0, duration, type = "sawtooth", peak = 0.25) {
  const c = getCtx();
  if (!c) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freqStart, t0);
  osc.frequency.exponentialRampToValueAtTime(Math.max(freqEnd, 1), t0 + duration);
  envelope(gain, t0, 0.02, duration, peak);
  osc.connect(gain).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
}

const PATCHES = {
  ship_horn: (c, t) => {
    tone(110, t, 1.0, "sawtooth", 0.4);
    tone(220, t, 1.0, "sawtooth", 0.25);
    tone(55, t, 1.2, "sine", 0.3);
  },
  trumpet: (c, t) => {
    // Elephant-style trumpet: descending bray
    sweep(800, 200, t, 0.6, "sawtooth", 0.4);
    sweep(400, 100, t + 0.1, 0.7, "square", 0.2);
    noiseBurst(t, 0.4, 0.1, 600);
  },
  rocket: (c, t) => {
    // Whoosh then rumble
    noiseBurst(t, 2.0, 0.35, 200);
    sweep(80, 800, t, 1.5, "sawtooth", 0.3);
  },
  cheer: (c, t) => {
    // Crowd cheer = wide-spectrum noise + a few sparkle tones
    noiseBurst(t, 1.2, 0.3, 1500);
    for (let i = 0; i < 6; i++) {
      tone(800 + Math.random() * 1200, t + i * 0.08, 0.15, "triangle", 0.1);
    }
  },
  bird_chirp: (c, t) => {
    // Tropical bird: rapid chirps
    for (let i = 0; i < 8; i++) {
      const start = t + i * 0.1;
      sweep(2000 + Math.random() * 1000, 3000 + Math.random() * 1500, start, 0.08, "sine", 0.25);
    }
  },
  train_whistle: (c, t) => {
    tone(800, t, 0.8, "triangle", 0.35);
    tone(1000, t, 0.8, "triangle", 0.25);
    sweep(800, 500, t + 0.6, 0.6, "triangle", 0.3);
    // chug chug
    for (let i = 0; i < 4; i++) noiseBurst(t + 1.2 + i * 0.25, 0.15, 0.1, 300);
  },
  cash_register: (c, t) => {
    tone(1800, t, 0.08, "square", 0.3);
    tone(2200, t + 0.08, 0.08, "square", 0.3);
    tone(2600, t + 0.16, 0.08, "square", 0.3);
    noiseBurst(t + 0.3, 0.4, 0.15, 2000);
  },
  slot_machine: (c, t) => {
    // Spin sound + jackpot bells
    for (let i = 0; i < 12; i++) tone(600 + (i % 3) * 200, t + i * 0.06, 0.05, "square", 0.2);
    for (let i = 0; i < 5; i++) tone(1500, t + 1.0 + i * 0.15, 0.2, "sine", 0.3);
  },
  sword_clang: (c, t) => {
    tone(2400, t, 0.4, "triangle", 0.45);
    tone(3600, t, 0.3, "triangle", 0.3);
    noiseBurst(t, 0.25, 0.25, 4000);
  },
  church_bell: (c, t) => {
    // Bell = fundamental + inharmonic partials with long decay
    const f = 220;
    [1, 2.4, 3.5, 4.7].forEach((mul, i) => {
      const osc = (getCtx() || {}).createOscillator?.();
      if (!osc) return;
      const ctxx = getCtx();
      const gain = ctxx.createGain();
      osc.type = "sine";
      osc.frequency.value = f * mul;
      envelope(gain, t, 0.01, 2.5 - i * 0.3, 0.35 / (i + 1));
      osc.connect(gain).connect(ctxx.destination);
      osc.start(t);
      osc.stop(t + 2.5);
    });
  },
  dolphin_click: (c, t) => {
    // Rapid click-chirps with rising pitch — playful, distinctive
    for (let i = 0; i < 6; i++) {
      sweep(2400 + i * 200, 4000 + i * 300, t + i * 0.07, 0.05, "sine", 0.3);
    }
    sweep(3500, 1800, t + 0.5, 0.2, "sine", 0.2);
  },
  wave_crash: (c, t) => {
    // Filtered noise swell — beach waves
    noiseBurst(t, 1.6, 0.35, 600);
    noiseBurst(t + 0.4, 0.8, 0.25, 350);
    sweep(120, 60, t, 1.5, "sine", 0.2);
  },
  fanfare: (c, t) => {
    // Royal trumpet arpeggio + sustain
    const notes = [523, 659, 784, 1047];
    notes.forEach((freq, i) => {
      tone(freq, t + i * 0.12, 0.5, "sawtooth", 0.3);
    });
    tone(1047, t + 0.6, 1.2, "sawtooth", 0.35);
  },
  shimmer: (c, t) => {
    // Glittering star — high harmonics ascending
    for (let i = 0; i < 12; i++) {
      const freq = 1600 + Math.random() * 2400;
      tone(freq, t + i * 0.05, 0.1, "triangle", 0.15);
    }
    tone(1320, t + 0.7, 0.6, "sine", 0.25);
  },
};

/**
 * Play a synthesised SFX patch for a given sound_kind string.
 * Falls back to a soft "ding" if the kind is unknown.
 */
export function playGiftSound(soundKind) {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") c.resume().catch(() => {});
  const t = c.currentTime + 0.02;
  const patch = PATCHES[soundKind];
  if (patch) {
    try { patch(c, t); } catch (e) { /* ignore */ }
  } else {
    // Default: soft chime
    tone(880, t, 0.4, "sine", 0.3);
    tone(1320, t + 0.05, 0.3, "sine", 0.2);
  }
}
